import { memo, useState, useEffect, useRef } from "react";
import { NumericFormat } from "react-number-format";
import { FormGroup } from "@mui/material";
import IconButton from "@mui/joy/IconButton";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import { UserSquare } from "@zenith/assets/icons/components";
import {
  TextFieldComponent,
  TextAreaComponent,
  ButtonComponent,
  AutocompleteComponent,
} from "@zenith/components";
import { AutocompleteSelectItemsType } from "@zenith/components/ui/AutocompleteComponent/autocomplete-component/interfaces";
import { FormDataType, FormErrorsType } from "../types";
import { ERROR_MESSAGES } from "../constants";
import { MAX_BENEFICIARY_ACCOUNT_NUMBER_LENGTH } from "@zenith/data/constants";
import { sortedBeneficiaryDetailsRecord } from "@zenith/pages/BeneficiariesPage/beneficiaries-page/beneficiaries-page";
import BeneficiaryList from "./BeneficiaryList";
import { CheckBox } from "@zenith/components/ui/CheckBoxComponent/checkbox-component";

interface BankTransferFormProps {
  formData: FormDataType;
  formErrors: FormErrorsType;
  bankList: AutocompleteSelectItemsType[];
  isLoadingBanks?: boolean;
  bankListError?: boolean;
  categoryList: AutocompleteSelectItemsType[];
  beneficiaryList?: sortedBeneficiaryDetailsRecord[];
  selectedBeneficiary?: sortedBeneficiaryDetailsRecord;
  isLoadingBeneficiaries: boolean;
  beneficiariesError: boolean;
  showBeneficiaryList: boolean;
  isLoading: boolean;
  availableLimit: number;
  availableBalance: number;
  saveBeneficiary: boolean;
  onSubmit: () => void;
  onSaveBeneficiaryChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onFormDataChange: (field: keyof FormDataType, value: string | number) => void;
  onBeneficiarySelect: (beneficiary: sortedBeneficiaryDetailsRecord) => void;
  onShowBeneficiaryList: () => void;
  validateAccountNumber?: (accountNumber: string) => Promise<boolean>;
  validateAmount?: (amount: number) => void;
  isValidating?: boolean;
}

const BankTransferForm = memo(function BankTransferForm({
  formData,
  formErrors,
  bankList,
  isLoadingBanks = false,
  bankListError = false,
  categoryList,
  beneficiaryList,
  selectedBeneficiary,
  isLoadingBeneficiaries,
  beneficiariesError,
  showBeneficiaryList,
  availableLimit,
  availableBalance,
  saveBeneficiary,
  isLoading,
  onSubmit,
  onSaveBeneficiaryChange,
  onFormDataChange,
  onBeneficiarySelect,
  onShowBeneficiaryList,
  validateAccountNumber,
  validateAmount,
  isValidating = false,
}: BankTransferFormProps) {
  const [isBeneficiarySaved, setIsBeneficiarySaved] = useState(saveBeneficiary);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleClickAway = () => {
    if (showBeneficiaryList) {
      onShowBeneficiaryList();
    }
  };

  useEffect(() => {
    setIsBeneficiarySaved(saveBeneficiary);
  }, [saveBeneficiary]);

  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value.replace(/[^\d.]/g, "");
    const numericValue = parseFloat(rawValue) || 0;
    onFormDataChange("amount", numericValue);
    if (validateAmount) {
      validateAmount(numericValue);
    }
  };

  const handleAccountNumberChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value.replace(/\D/g, "");
    onFormDataChange("beneficiaryAccountNumber", value);
  };

  const handleBankChange = (value: string, label: string) => {
    onFormDataChange("bankId", value);
    onFormDataChange("bankName", label);
    onFormDataChange("beneficiaryName", "");
  };

  const handleBankInputChange = (value: string) => {
    onFormDataChange("bankName", value);
    if (formData.bankId) onFormDataChange("bankId", "");
    onFormDataChange("beneficiaryName", "");
  };

  const handleCategoryChange = (value: string, label: string) => {
    onFormDataChange("categoryId", value);
    onFormDataChange("categoryName", label);
  };

  const handleCategoryInputChange = (value: string) => {
    onFormDataChange("categoryName", value);
    if (formData.categoryId) onFormDataChange("categoryId", "");
  };

  const handleBeneficiarySelect = (
    beneficiary: sortedBeneficiaryDetailsRecord
  ) => {
    onBeneficiarySelect(beneficiary);
    setIsBeneficiarySaved(true);
  };

  const isSubmitDisabled = () => {
    const hasRequiredFields =
      formData.amount > 0 &&
      formData.beneficiaryAccountNumber.length ===
        MAX_BENEFICIARY_ACCOUNT_NUMBER_LENGTH &&
      formData.bankId &&
      formData.beneficiaryName;

    const hasBlockingErrors =
      isValidating ||
      formErrors.beneficiaryAccountNumber.hasError ||
      formErrors.bankName.hasError ||
      formErrors.amount.hasError;

    return !(hasRequiredFields && !hasBlockingErrors);
  };

  // Show loading indicator when validating
  const showValidatingIndicator =
    isValidating &&
    formData.beneficiaryAccountNumber.length === 10 &&
    !formData.beneficiaryName;

  return (
    <>
      <div className="mb-8 flex flex-col gap-4">
        <div className="relative" ref={containerRef}>
          <ClickAwayListener onClickAway={handleClickAway}>
            <div>
              <TextFieldComponent
                id="beneficiary-account-number-field"
                label="Beneficiary Account Number"
                labelId="beneficiary-account-number-field-label"
                placeholder="Enter an account number or select a beneficiary"
                type="tel"
                className={`border-neutral-300 pr-4 pl-4 shadow-none before:shadow-neutral-900 ${
                  showBeneficiaryList
                    ? "border-b-0 before:shadow-none [&.fieldset]:border-0 [&.text-field]:rounded-b-none"
                    : ""
                }`}
                value={formData.beneficiaryAccountNumber}
                hasError={formErrors.beneficiaryAccountNumber.hasError}
                validationMessage={formErrors.beneficiaryAccountNumber.message}
                maxLength={10}
                onChange={handleAccountNumberChange}
                endDecorator={
                  <div className="flex items-center gap-2">
                    <IconButton color="neutral" onClick={onShowBeneficiaryList}>
                      <UserSquare width={24} height={24} />
                    </IconButton>
                  </div>
                }
              />
              {showBeneficiaryList && (
                <div className="absolute z-[1300] w-full rounded-b-xl border border-t-0 border-neutral-300 bg-white p-3 pt-0">
                  <hr />
                  <BeneficiaryList
                    isLoading={isLoadingBeneficiaries}
                    hasError={beneficiariesError}
                    beneficiaries={beneficiaryList}
                    selectedBeneficiary={selectedBeneficiary}
                    onSelect={handleBeneficiarySelect}
                  />
                </div>
              )}
            </div>
          </ClickAwayListener>
        </div>

        <div
          className={
            formErrors.beneficiaryAccountNumber.message ===
            ERROR_MESSAGES.SAME_ACCOUNT_TRANSFER
              ? "pointer-events-none opacity-40"
              : ""
          }
        >
          <AutocompleteComponent
            id="send-money-bank-name"
            label="Bank Name"
            labelId="bank-name-dropdown-label"
            options={bankList || []}
            isLoading={isLoadingBanks}
            noOptionsTitle={
              bankListError ? "We are unable to fetch bank list" : undefined
            }
            noOptionsSubtitle={
              bankListError
                ? "Please try again. Apologies for the inconvenience."
                : undefined
            }
            selectValue={
              formData.bankId && formData.bankName
                ? { label: formData.bankName, value: formData.bankId }
                : null
            }
            placeholder="Select bank name"
            hasError={formErrors.bankName.hasError}
            errorMessage={formErrors.bankName.message}
            onChange={handleBankChange}
            onInputChange={handleBankInputChange}
            className="w-full"
            showSearchIcon
            trailingLoading={showValidatingIndicator}
          />
        </div>

        <TextFieldComponent
          id="beneficiary-name-field"
          label="Beneficiary Name"
          labelId="beneficiary-name-field-label"
          className="h-12 disabled:text-neutral-600"
          placeholder="Beneficiary name will appear here"
          type="text"
          value={formData.beneficiaryName}
          disabled
          disabledTextColor="#626262"
          onChange={(_: React.ChangeEvent<HTMLInputElement>) => void 0}
        />

        <FormGroup className="w-max">
          <CheckBox
            label="Save Beneficiary"
            state={isBeneficiarySaved ? "checked" : "unchecked"}
            onChange={(nextState) => {
              const checked = nextState === "checked";
              setIsBeneficiarySaved(checked);
              onSaveBeneficiaryChange({
                target: { checked },
              } as React.ChangeEvent<HTMLInputElement>);
            }}
          />
        </FormGroup>
      </div>

      <hr className="bg-neutral-600" />

      <h2 className="mt-6 mb-4 text-xl font-medium text-neutral-900">
        Transfer Details
      </h2>

      <div className="mb-4 flex flex-col gap-4">
        <div>
          <div className="ml-2 mb-1 text-sm text-neutral-500">Amount</div>
          <div className="relative">
            <NumericFormat
              allowNegative={false}
              placeholder="0.00"
              value={formData.amount || ""}
              id="transfer-details-amount-input"
              className={`h-12 w-full rounded-1.5lg border px-4 py-3 pl-11 text-lg leading-normal text-black ${
                formErrors.amount.hasError ? "border-red-500" : ""
              }`}
              decimalScale={2}
              fixedDecimalScale
              onChange={handleAmountChange}
              allowLeadingZeros={false}
              thousandSeparator={true}
              valueIsNumericString
              isAllowed={(values) => {
                const { floatValue } = values;
                return (
                  floatValue === undefined || floatValue.toString().length <= 16
                );
              }}
            />
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-lg">
              ₦
            </span>
          </div>
          {formErrors.amount.hasError && (
            <div className="mx-1.5 mt-1 text-sm text-red-400">
              {formErrors.amount.message}
            </div>
          )}
        </div>

        <AutocompleteComponent
          id="send-money-category"
          label="Category (optional)"
          labelId="category-dropdown-label"
          options={categoryList || []}
          selectValue={
            formData.categoryId && formData.categoryName
              ? { label: formData.categoryName, value: formData.categoryId }
              : null
          }
          placeholder="Select a category"
          onChange={handleCategoryChange}
          onInputChange={handleCategoryInputChange}
          className="w-full"
          showSearchIcon
        />

        <TextAreaComponent
          minRows={3}
          id="send-money-textarea"
          labelId="send-money-textarea-label"
          label="Description (optional)"
          placeholder="Enter a description with a maximum of 50 characters"
          value={formData.description}
          onChange={(e) => onFormDataChange("description", e.target.value)}
          maxLength={50}
        />
      </div>

      <div className="mt-3 flex w-full justify-center">
        <ButtonComponent
          label="Continue"
          onClick={onSubmit}
          disabled={isSubmitDisabled() || isLoading}
          isLoading={isLoading}
          size="large"
          type="primary"
          id="send-money-button"
          fullWidth
        />
      </div>
    </>
  );
});

export default BankTransferForm;
